import { useState, useEffect, useRef, useCallback } from 'react';
import { FaBars, FaTimes, FaSearch } from 'react-icons/fa';
import styles from './Header.module.scss';
import { Link, useLocation } from 'react-router-dom';
// Update this path if your logo has a different name/location
import Logo from 'assets/BizSolutions Logo_No background (use for ServiceNow Branding).png';
import { PRACTICES, practicePath, servicePath } from '../../content/services';

// Grace period before a dropdown closes on mouseleave. Without it, any
// diagonal cursor movement toward the panel closed it before arrival.
const CLOSE_DELAY_MS = 180;

interface PreviewLink {
  text: string;
  path: string;
}

/**
 * A practice column in the Services panel. The heading is a link to the hub,
 * not a label -- the hub is a real page and the column head is the obvious
 * place to reach it from.
 */
interface PreviewColumn {
  heading: PreviewLink;
  description: string;
  links: PreviewLink[];
}

interface NavItem {
  title: string;
  path: string;
  preview: {
    /** Single-list panels (Insights, Events, About, Careers). */
    title?: string;
    description?: string;
    links?: PreviewLink[];
    /** Column panels (Services). Takes the place of title/description/links. */
    columns?: PreviewColumn[];
  };
}

const Header = () => {
  const location = useLocation();
  const [openNav, setOpenNav] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggerRefs = useRef<Record<string, HTMLAnchorElement | null>>({});
  // Set while Escape returns focus to the trigger, so the resulting focus event
  // doesn't immediately reopen the panel the user just dismissed.
  const dismissingRef = useRef(false);

  const cancelClose = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const openPreview = useCallback((path: string) => {
    cancelClose();
    setOpenNav(path);
  }, [cancelClose]);

  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => setOpenNav(null), CLOSE_DELAY_MS);
  }, [cancelClose]);

  const closeNow = useCallback(() => {
    cancelClose();
    setOpenNav(null);
  }, [cancelClose]);

  // Don't leave a pending close timer behind on unmount.
  useEffect(() => cancelClose, [cancelClose]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setIsScrolled(scrollTop > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu and any open dropdown on navigation.
  const closeMenus = useCallback(() => {
    setMobileMenuOpen(false);
    closeNow();
  }, [closeNow]);

  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    closeMenus();
  };

  const handlePreviewLinkClick = (path: string) => {
    closeMenus();

    const [pathname, hash] = path.split('#');

    // Different route: the <Link> performs the navigation and the effect below
    // scrolls to the hash once the new page has rendered. This used to assign
    // window.location.href, which forced a full page reload and threw away the
    // SPA -- refetching the bundle on every dropdown link.
    if (pathname && pathname !== location.pathname) return;

    // Same route: clicking the link the user is already on doesn't change
    // `location`, so the effect won't fire. Scroll directly.
    if (hash) {
      document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (hash) {
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [location]);

  // The Services panel is one column per practice, built from PRACTICES --
  // so a service added to the taxonomy appears here without a second edit.
  // The trigger itself already links to /services, which is why the panel
  // does not repeat an "All Services" row.
  const navItems: NavItem[] = [
    {
      title: 'Services',
      path: '/services',
      preview: {
        columns: PRACTICES.map((practice) => ({
          heading: { text: practice.name, path: practicePath(practice) },
          description: practice.navDescription,
          links: practice.services.map((service) => ({
            text: service.navLabel,
            path: servicePath(service)
          }))
        }))
      }
    },
    {
      title: 'Insights',
      path: '/insights',
      preview: {
        title: 'Latest Insights',
        description: 'Explore our articles on technology trends and industry innovations',
        links: [
          { text: 'AI Trends', path: '/insights#ai' }, // Changed from '/blog#ai'
          { text: 'ServiceNow Updates', path: '/insights#servicenow' }, // Changed from '/blog#servicenow'
          { text: 'Digital Transformation', path: '/insights#digital' }, // Changed from '/blog#digital'
          { text: 'Case Studies', path: '/insights#cases' } // Changed from '/blog#cases'
        ]
      }
    },
    {
      title: 'Events',
      path: '/events',
      preview: {
        title: 'Upcoming Events',
        description: 'Join us at industry conferences and networking events',
        links: [
          { text: 'Upcoming Events', path: '/events#upcoming' },
          { text: 'Past Events', path: '/events#past' },
          { text: 'Stay Informed', path: '/events#newsletter' }
        ]
      }
    },
    {
      title: 'About',
      path: '/about',
      preview: {
        title: 'Our Company',
        description: 'Learn about our mission, leadership and career opportunities',
        links: [
          { text: 'Our Mission', path: '/about#mission' },
          { text: 'Our Values', path: '/about#values' },
          { text: 'Leadership', path: '/about#leadership' },
          { text: 'Our Culture', path: '/about#culture' },
          { text: 'Our Journey', path: '/about#journey' }
        ]
      }
    },
    {
      title: 'Careers',
      path: '/careers',
      preview: {
        title: 'Join Our Team',
        description: 'Explore career opportunities and grow with us',
        links: [
          { text: 'Grow With Us', path: '/careers#growWithUs' },
          { text: 'Join Our Team', path: '/careers#joinUs' }
        ]
      }
    }
  ];

  return (
    <header className={`${styles.header} ${isScrolled ? styles.scrolled : ''}`}>
      <div className={styles.container}>
        <div className={styles.logo}>
          <Link to="/" onClick={() => setMobileMenuOpen(false)}>
            <img src={Logo} alt="BizSolutions.Tech Logo" className={styles.logoImage} />
          </Link>
        </div>

        {/* Mobile Menu Toggle */}
        <button 
          className={styles.mobileMenuToggle}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle navigation"
          aria-expanded={mobileMenuOpen}
        >
          {mobileMenuOpen ? <FaTimes /> : <FaBars />}
        </button>
        
        {/* Main Navigation */}
        <nav className={`${styles.nav} ${mobileMenuOpen ? styles.mobileOpen : ''}`}>
          <ul className={styles.navList}>
            <li className={styles.navItem}>
              <Link 
                to="/" 
                className={`${styles.navLink} ${location.pathname === '/' ? styles.active : ''}`}
                onClick={() => handleScrollToTop()}
              >
                Home
              </Link>
            </li>
            
            {navItems.map((item) => {
              const previewId = `nav-preview-${item.path.replace(/\W+/g, '-')}`;
              const isOpen = openNav === item.path || mobileMenuOpen;

              return (
                <li
                  key={item.path}
                  // The trigger and the panel share this one hover container, so
                  // moving between them never leaves the hovered element.
                  className={`${styles.navItem} ${isOpen ? styles.open : ''}`}
                  onMouseEnter={() => openPreview(item.path)}
                  onMouseLeave={scheduleClose}
                  onFocus={() => {
                    if (dismissingRef.current) return;
                    openPreview(item.path);
                  }}
                  onBlur={(e) => {
                    // Only close once focus has left the item entirely.
                    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                      closeNow();
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape' && openNav === item.path) {
                      e.stopPropagation();
                      closeNow();
                      // Focus events dispatch synchronously, so the guard is
                      // consumed (or unused) by the time we clear it.
                      dismissingRef.current = true;
                      triggerRefs.current[item.path]?.focus();
                      dismissingRef.current = false;
                    }
                  }}
                >
                  <Link
                    to={item.path}
                    ref={(el) => { triggerRefs.current[item.path] = el; }}
                    className={`${styles.navLink} ${location.pathname === item.path ? styles.active : ''}`}
                    aria-haspopup="true"
                    aria-expanded={isOpen}
                    aria-controls={previewId}
                    onClick={() => handleScrollToTop()}
                  >
                    {item.title}
                  </Link>

                  {/* Always rendered: unmounting on mouseleave skipped the exit
                      transition and made the panel unreachable by keyboard.
                      While closed it is `visibility: hidden`, which also takes
                      its links out of the tab order. */}
                  <div
                    className={`${styles.navPreview} ${item.preview.columns ? styles.wide : ''}`}
                    id={previewId}
                  >
                    <div className={styles.previewContent}>
                      {item.preview.columns ? (
                        // One column per practice. The column head is a link
                        // to the hub, so the hierarchy in the panel matches
                        // the hierarchy in the URL.
                        <div className={styles.previewColumns}>
                          {item.preview.columns.map((column) => (
                            <div key={column.heading.path} className={styles.previewColumn}>
                              <h3>
                                <Link
                                  to={column.heading.path}
                                  onClick={() => handlePreviewLinkClick(column.heading.path)}
                                >
                                  {column.heading.text}
                                </Link>
                              </h3>
                              <p>{column.description}</p>
                              <ul className={styles.previewLinks}>
                                {column.links.map((link) => (
                                  <li key={link.path}>
                                    <Link
                                      to={link.path}
                                      onClick={() => handlePreviewLinkClick(link.path)}
                                    >
                                      {link.text}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <>
                          <h3>{item.preview.title}</h3>
                          <p>{item.preview.description}</p>
                          <ul className={styles.previewLinks}>
                            {item.preview.links?.map((link) => (
                              <li key={link.path}>
                                {/* Deliberately not handleScrollToTop(): these
                                    links target a section, and scrolling to the
                                    top fought the anchor scroll. */}
                                <Link
                                  to={link.path}
                                  onClick={() => handlePreviewLinkClick(link.path)}
                                >
                                  {link.text}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}

            {/* Contact is the button in the header bar on desktop. That button
                is hidden at the drawer breakpoint, where it crowded the
                hamburger, so the drawer carries the destination instead --
                otherwise hiding the button would strand /contact on mobile. */}
            <li className={`${styles.navItem} ${styles.mobileOnlyItem}`}>
              <Link
                to="/contact"
                className={`${styles.navLink} ${location.pathname === '/contact' ? styles.active : ''}`}
                onClick={() => handleScrollToTop()}
              >
                Contact Us
              </Link>
            </li>
          </ul>
        </nav>

        <div className={styles.headerActions}>
          <Link 
            to="/contact" 
            className={styles.contactButton}
            onClick={() => handleScrollToTop()}
          >
            Contact Us
          </Link>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className={styles.mobileOverlay} 
          onClick={() => setMobileMenuOpen(false)}
          role="presentation"
        />
      )}
    </header>
  );
};

export default Header;
